import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { TranslocoModule } from '@ngneat/transloco';
import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';
import { debounceTime } from 'rxjs';
import { provideSolvaceIcons } from '../svz-icons/solvace-icons';

@Component({
  standalone: true,
  selector: 'svz-search-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-search-field.component.html',
  imports: [ReactiveFormsModule, NgIcon, TranslocoModule, LibSharedCommonModule, NgClass],
  providers: [provideSolvaceIcons()],
})
export class SvzSearchFieldComponent {
  readonly initialValue = input<string | null>(null);

  readonly disabled = input(false);

  readonly inputId = input('');

  readonly inputTestId = input('');

  readonly magnifierTestId = input('');

  readonly minChars = input(3);

  readonly magnifierTriggersSearch = input(true);

  /** Additive, defaults to current behavior (magnifier icon changes color on hover). Set true to
   *  keep the magnifier's icon color fixed on hover — matches prototypes whose search box icon is a
   *  single static color (`.s-ico{color:#717182}`, no hover rule) — see RCA Notification Management. */
  readonly magnifierStaticColor = input(false);

  /** Additive. Field height: `md` 40px (search toolbars, `.toolbar .search-box .input`) or `lg` 44px (the design-system
   *  `.input` default, used by the analytics toolbars). */
  readonly size = input<'md' | 'lg'>('md');

  /** Additive. `design-system` draws the prototypes' `.input{ border-radius:8px }`; `default` keeps the
   *  existing `rounded-lg`, which some app radius scales resolve to 10px. */
  readonly shape = input<'default' | 'design-system'>('default');

  protected readonly radiusClass = computed(() => (this.shape() === 'design-system' ? 'rounded-[8px]' : 'rounded-lg'));

  readonly valueChange = output<string>();

  readonly formControl = new FormControl<string>('', { nonNullable: true });

  private lastFormControlValue = '';
  private forceNextEmit = false;

  get value(): string {
    return this.formControl.value;
  }

  constructor() {
    effect(() => {
      const initialValue = this.initialValue() ?? '';
      untracked(() => {
        this.formControl.setValue(initialValue, { emitEvent: false });
        this.lastFormControlValue = initialValue;
      });
    });

    effect(() => {
      const disabled = this.disabled();
      untracked(() => {
        if (disabled && this.formControl.enabled) {
          this.formControl.disable({ emitEvent: false });
        } else if (!disabled && this.formControl.disabled) {
          this.formControl.enable({ emitEvent: false });
        }
      });
    });

    this.formControl.valueChanges.pipe(debounceTime(500), takeUntilDestroyed()).subscribe((value) => {
      const shouldForce = this.forceNextEmit;
      this.forceNextEmit = false;
      this.emitWhenAllowed({ value, force: shouldForce });
    });
  }

  forceEmit(): void {
    this.emitWhenAllowed({ force: true });
  }

  enable(options?: { emitEvent?: boolean }): void {
    this.forceNextEmit = options?.emitEvent ?? false;
    this.formControl.enable({ emitEvent: options?.emitEvent });
  }

  disable(options?: { emitEvent?: boolean }): void {
    this.forceNextEmit = options?.emitEvent ?? false;
    this.formControl.disable({ emitEvent: options?.emitEvent });
  }

  setValue(value: string, options?: { emitEvent?: boolean }): void {
    this.forceNextEmit = options?.emitEvent ?? false;
    this.formControl.setValue(value, { emitEvent: options?.emitEvent });
  }

  reset(): void {
    this.setValue(this.initialValue() ?? '', { emitEvent: false });
  }

  private emitWhenAllowed(options?: { value?: string; force?: boolean }): void {
    if (this.formControl.disabled) {
      return;
    }

    const force = options?.force ?? false;
    const typed = options?.value ?? this.formControl.value;
    const belowMinimum = typed.length > 0 && typed.length < this.minChars();
    const value = !force && belowMinimum ? '' : typed;
    if (force || value !== this.lastFormControlValue) {
      this.lastFormControlValue = value;
      this.valueChange.emit(value);
    }
  }
}
